import { Download, Loader2, Paperclip, Trash2, Upload } from "lucide-react";
import { useRef, useState } from "react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import {
  type AuditFileRow,
  openAuditFileDownload,
  useAuditFiles,
  useDeleteAuditFile,
  useUploadAuditFiles,
} from "@/hooks/use-audit-files";
import { ACCEPTED_AUDIT_FILE_TYPES, MAX_AUDIT_FILE_SIZE_BYTES } from "@/lib/audit-file-limits";
import { formatDateTime, formatFileSize } from "@/lib/format";

interface AuditFilesButtonProps {
  auditId: string;
  canUpload: boolean;
}

export function AuditFilesButton({ auditId, canUpload }: AuditFilesButtonProps) {
  const [open, setOpen] = useState(false);
  const { data: files } = useAuditFiles(auditId);
  const count = files?.length ?? 0;

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
        <Paperclip className="h-4 w-4 mr-1.5" />
        Attachments
        {count > 0 && (
          <Badge variant="secondary" className="ml-1.5 px-1.5 py-0 text-xs">
            {count}
          </Badge>
        )}
      </Button>
      <AuditFilesDialog
        open={open}
        onOpenChange={setOpen}
        auditId={auditId}
        canUpload={canUpload}
      />
    </>
  );
}

function AuditFilesDialog({
  open,
  onOpenChange,
  auditId,
  canUpload,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  auditId: string;
  canUpload: boolean;
}) {
  const { data: files = [], isLoading } = useAuditFiles(auditId);
  const upload = useUploadAuditFiles(auditId);
  const deleteFile = useDeleteAuditFile(auditId);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  function handleFilesSelected(e: React.ChangeEvent<HTMLInputElement>) {
    const selected = Array.from(e.target.files ?? []);
    e.target.value = "";
    if (selected.length === 0) return;

    const oversized = selected.filter((f) => f.size > MAX_AUDIT_FILE_SIZE_BYTES);
    if (oversized.length > 0) {
      const limit = MAX_AUDIT_FILE_SIZE_BYTES / (1024 * 1024);
      toast.error(
        oversized.length === 1
          ? `"${oversized[0]?.name}" exceeds the ${limit}MB limit`
          : `${oversized.length} files exceed the ${limit}MB limit`,
      );
      return;
    }

    upload.mutate(selected);
  }

  async function handleDownload(file: AuditFileRow) {
    setDownloadingId(file.id);
    try {
      await openAuditFileDownload(auditId, file.id);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to download attachment");
    } finally {
      setDownloadingId(null);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Attachments</DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-2 py-2 min-h-0">
          {isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : files.length === 0 ? (
            <p className="text-sm text-muted-foreground">No attachments yet.</p>
          ) : (
            files.map((file) => (
              <div
                key={file.id}
                className="flex items-center gap-3 rounded-md border border-border bg-muted/30 p-3"
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{file.fileName}</p>
                  <p className="text-xs text-muted-foreground">
                    {formatFileSize(file.sizeBytes)} · {file.uploadedByName ?? "Unknown"} ·{" "}
                    {formatDateTime(file.createdAt)}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="shrink-0"
                  disabled={downloadingId === file.id}
                  onClick={() => handleDownload(file)}
                  title="Download"
                >
                  {downloadingId === file.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4" />
                  )}
                </Button>
                {canUpload && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="shrink-0 text-muted-foreground hover:text-destructive"
                    disabled={deleteFile.isPending}
                    onClick={() => deleteFile.mutate(file.id)}
                    title="Delete"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            ))
          )}
        </div>

        {canUpload && (
          <div className="border-t pt-3">
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={ACCEPTED_AUDIT_FILE_TYPES}
              className="hidden"
              onChange={handleFilesSelected}
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="w-full"
              disabled={upload.isPending}
              onClick={() => fileInputRef.current?.click()}
            >
              {upload.isPending ? (
                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
              ) : (
                <Upload className="h-4 w-4 mr-1.5" />
              )}
              {upload.isPending ? "Uploading…" : "Upload files"}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
